import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, exhaustMap, catchError, filter, take, scan } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import * as AppActions from './app.actions';
import { WINDOW } from "../../window.provider";
import { Theme } from "../../shared/models/theme";
import { of } from "rxjs";
import * as CoreActions from "../core/core.actions";
import { StyleHelper } from "../../helpers/style-helper";
import { Icon } from 'src/app/shared/models/icon';

@Injectable()
export class AppEffects {

  private windowInternal: Window;

  constructor(
    private readonly actions$: Actions,
    private readonly apiService: ApiService,
    @Inject(WINDOW) private window: Window
  ) {
    this.windowInternal = window;
  }

  readonly trackInitialization$ = createEffect(() => {
    const requiredActions = [
      AppActions.setConfiguration.type,
      AppActions.setTheme.type,
      AppActions.setIcons.type
    ];
    
    return this.actions$.pipe(
      ofType(
        AppActions.setConfiguration,
        AppActions.setTheme,
        AppActions.setIcons
      ),
      scan((completedActions, action) => {
        return [...completedActions, action.type];
      }, [] as string[]),
      map(completedActions => {
        const allActionsCompleted = requiredActions.every(
          actionType => completedActions.includes(actionType)
        );
        return allActionsCompleted;
      }),
      filter(isComplete => isComplete),
      take(1),
      map(() => AppActions.initializationCompleted())
    );
  });

  readonly getConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getConfiguration),
        exhaustMap(() =>
          this.apiService.getConfiguration().pipe(
            map(config => AppActions.setConfiguration(config))
          )
        )
      ),
    {dispatch: true}
  );

  readonly updateHostname$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updateHostname),
      exhaustMap(() =>
        this.apiService.getTheme(this.windowInternal.location.hostname).pipe(
          map((res: Theme) => {
            StyleHelper.applyCustomStyle(res);
            return AppActions.setTheme({theme: res});
          }),
          catchError(err => {
            return of(CoreActions.setError({error: err?.error?.Message ? err.error.Message : 'customError'}))
          })
        )
      )
    ),
    { dispatch: true }
  );

  readonly initIcons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.initIcons),
      exhaustMap(() => this.apiService.getIcons().pipe()),
      map((res: Icon[]) => AppActions.setIcons({ icons: res })),
      catchError(err => {
        return of(CoreActions.setError({error: err?.error?.Message ? err.error.Message : 'customError'}))
      })
    ),
    { dispatch: true }
  );
}

